import { useEffect, useRef, useState } from 'react';
import type { WisteriaSway } from '@/lib/heroMotion/wisteriaSway';
import { whenIdleAfterLoad } from '@/lib/idle';
import { cn } from '@/lib/utils';

const START_TIMEOUT_MS = 5000;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const SLOW_CONNECTIONS = ['slow-2g', '2g', '3g'];

interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

/** Decoration only: skipped for reduced motion, Data Saver and slow connections (it downloads up to ~245 KB). */
function heroMotionAllowed(): boolean {
  if (window.matchMedia?.(REDUCED_MOTION_QUERY)?.matches) return false;
  const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  if (connection?.saveData) return false;
  return !SLOW_CONNECTIONS.includes(connection?.effectiveType ?? '');
}

interface HeroWisteriaMotionProps {
  /** Hold off until other idle-time work (the wizard preload) has gone first */
  canStart?: boolean;
  /** Hold the current frame, e.g. while the quote wizard covers the hero */
  paused?: boolean;
  /** Which photo it runs on: the landing's wisteria, or a band in the Mi boda banner (ceremony, salón) */
  scene?: 'wisteria' | 'ceremony' | 'salon';
}

const loadSway = (scene: 'wisteria' | 'ceremony' | 'salon') =>
  scene === 'salon'
    ? import('@/lib/heroMotion/salonSway').then((m) => m.startSalonSway)
    : scene === 'ceremony'
      ? import('@/lib/heroMotion/ceremonySway').then((m) => m.startCeremonySway)
      : import('@/lib/heroMotion/wisteriaSway').then((m) => m.startWisteriaSway);

/**
 * Soft wind sway on the hero's hanging wisteria. Nothing renders until the page has loaded and gone idle, so the
 * photo stays the first paint; then the WebGL code and its assets load as a separate chunk and the canvas fades in
 * over the photo from a still frame. Any failure just removes the canvas.
 */
export function HeroWisteriaMotion({ canStart = true, paused = false, scene = 'wisteria' }: HeroWisteriaMotionProps) {
  const [active, setActive] = useState(false);
  const [ready, setReady] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const swayRef = useRef<WisteriaSway | undefined>(undefined);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    if (!canStart || !heroMotionAllowed()) return;
    const cancel = whenIdleAfterLoad(() => setActive(true), START_TIMEOUT_MS);
    // Turning on reduced motion mid-visit stops it, or keeps it from starting
    const reducedMotion = window.matchMedia?.(REDUCED_MOTION_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      if (!event.matches) return;
      cancel();
      setActive(false);
    };
    reducedMotion?.addEventListener?.('change', onChange);
    return () => {
      cancel();
      reducedMotion?.removeEventListener?.('change', onChange);
    };
  }, [canStart]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const fail = () => setActive(false);
    loadSway(scene)
      .then((startSway) => {
        if (cancelled || !canvasRef.current) return;
        swayRef.current = startSway(canvasRef.current, { onReady: () => setReady(true), onFail: fail });
        swayRef.current.setPaused(pausedRef.current);
      })
      .catch(fail);
    return () => {
      cancelled = true;
      swayRef.current?.stop();
      swayRef.current = undefined;
      setReady(false);
    };
    // a different scene restarts the sway on its own photo and mask
  }, [active, scene]);

  useEffect(() => {
    swayRef.current?.setPaused(paused);
  }, [paused]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid="hero-wisteria-motion"
      className={cn(
        'pointer-events-none absolute inset-x-0 top-0 w-full transition-opacity duration-[600ms]',
        ready ? 'opacity-100' : 'opacity-0',
      )}
    />
  );
}
